import React, { useState, useEffect } from "react";
import { Button, Table, Tag, Space, message, Input } from "antd";
import CreateCredentialForm from "./CreateCredentialForm";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../../redux/CreateCredential/action";
import { SearchOutlined, CopyOutlined } from "@ant-design/icons";
import decryptedModulesData from "../../../utils/decryptedModules";

const CreateCrdential = ({ role }) => {
  const { modules: permissions } = decryptedModulesData();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [messageApi, contextHolder] = message.useMessage();
  const [editingRecord, setEditingRecord] = useState(null); // Add this line
  const dispatch = useDispatch();

  const { totalAgentsData, loading: reduxLoading } = useSelector(
    (state) => state?.AgentReducer,
  );

  const agentDetailsPermission =
    (Array.isArray(permissions) &&
      permissions?.find(
        (perm) => perm.module?.toLowerCase() === "agent details",
      )) ||
    {};

  useEffect(() => {
    dispatch(actions.getAgentsData());
  }, [dispatch]);

  const filteredData = (totalAgentsData || []).filter((agent) => {
    const text = searchText.toLowerCase();
    return (
      agent.companyName?.toLowerCase().includes(text) ||
      agent.orgId?.toString().toLowerCase().includes(text) ||
      agent.clientKey?.toLowerCase().includes(text)
    );
  });

  // Transform the data for the table
  const tableData = filteredData?.map((agent, index) => ({
    key: index + 1,
    orgId: agent.orgId,
    companyName: agent.companyName,
    phone: agent.phone,
    gst: agent.gst,
    type: "BILLER", // Default type, modify as needed
    status: agent.status || "Active", // Default to Active if status doesn't exist
    createdAt: agent.createdAt,
    _id: agent._id, // Include the unique identifier
    clientKey: agent.clientKey,
    clientSecret: agent.clientSecret || "Not available", // Default if not available
    scopes: agent.scopes,
    // Include any other fields you want to display
  }));

  const columns = [
    {
      title: "#",
      dataIndex: "key",
      key: "key",
      width: 50,
    },
    {
      title: "ORG ID",
      dataIndex: "orgId",
      key: "orgId",
      ellipsis: true,
    },
    {
      title: "COMPANY NAME",
      dataIndex: "companyName",
      key: "companyName",
    },
    {
      title: "PHONE",
      dataIndex: "phone",
      key: "phone",
      render: (phone) => phone || "-",
    },

    {
      title: "CLIENT KEY",
      dataIndex: "clientKey",
      key: "clientKey",
      render: (clientKey) => {
        if (!clientKey || clientKey === "-") return "-";
        const displayKey = clientKey.length > 16
          ? `${clientKey.slice(0, 8)}...${clientKey.slice(-8)}`
          : clientKey;
        return (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
            <span
              style={{
                filter: "blur(5px)",
                transition: "filter 0.2s ease-in-out",
                userSelect: "none",
                wordBreak: "break-all",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.filter = "none";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.filter = "blur(5px)";
              }}
            >
              {displayKey}
            </span>
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => {
                navigator.clipboard.writeText(clientKey)
                  .then(() => messageApi.success("Client Key copied!"))
                  .catch(() => messageApi.error("Failed to copy!"));
              }}
              style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            />
          </div>
        );
      },
    },
    {
      title: "Client Secret",
      dataIndex: "clientSecret",
      key: "clientSecret",
      render: (clientSecret) => {
        if (!clientSecret || clientSecret === "-") return "-";
        const displaySecret = clientSecret.length > 16
          ? `${clientSecret.slice(0, 8)}...${clientSecret.slice(-8)}`
          : clientSecret;
        return (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
            <span
              style={{
                filter: "blur(5px)",
                transition: "filter 0.2s ease-in-out",
                userSelect: "none",
                wordBreak: "break-all",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.filter = "none";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.filter = "blur(5px)";
              }}
            >
              {displaySecret}
            </span>
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => {
                navigator.clipboard.writeText(clientSecret)
                  .then(() => messageApi.success("Client Secret copied!"))
                  .catch(() => messageApi.error("Failed to copy!"));
              }}
              style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            />
          </div>
        );
      },
    },
    {
      title: "SCOPES",
      dataIndex: "scopes",
      key: "scopes",
      render: (scopes) => {
        if (!scopes || scopes === "-") return "-";
        const displayScopes = scopes.length > 20
          ? `${scopes.slice(0, 10)}...${scopes.slice(-10)}`
          : scopes;
        return (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
            <span
              style={{
                filter: "blur(5px)",
                transition: "filter 0.2s ease-in-out",
                userSelect: "none",
                wordBreak: "break-all",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.filter = "none";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.filter = "blur(5px)";
              }}
            >
              {displayScopes}
            </span>
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => {
                navigator.clipboard.writeText(scopes)
                  .then(() => messageApi.success("Scopes copied!"))
                  .catch(() => messageApi.error("Failed to copy!"));
              }}
              style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            />
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const canUpdate = agentDetailsPermission?.update ?? false;

        return (
          <Space size="middle">
            <Button
              type="link"
              icon={
                <span role="img" aria-label="edit">
                  ✏️
                </span>
              }
              disabled={!canUpdate}
              onClick={() => {
                if (canUpdate) {
                  setEditingRecord(record);
                  setVisible(true);
                }
              }}
              style={{
                padding: 0,
                opacity: canUpdate ? 1 : 0.9,
                cursor: canUpdate ? "pointer" : "not-allowed",
              }}
            />
          </Space>
        );
      },
    },
  ];

  return (
    <div className="mainContain">
      {contextHolder}

      <div className="d-flex justify-content-between align-items-center mb-3">
        {/* <Button
          style={{
            backgroundColor: "#073763",
            borderColor: "#073763",
            color: "white",
          }}
          size="large"
          onClick={() => setVisible(true)}
        >
          Add Organizer
        </Button> */}
        {agentDetailsPermission && (
          <Button
            style={{
              backgroundColor: "#073763",
              borderColor: "#073763",
              color: "white",
              opacity: agentDetailsPermission.create ? 1 : 0.6, // Optional: make it look disabled
              cursor: agentDetailsPermission.create ? "pointer" : "not-allowed",
            }}
            size="large"
            disabled={!agentDetailsPermission.create} // Disable if no create permission
            onClick={() => {
              if (agentDetailsPermission.create) {
                setVisible(true);
              }
            }}
          >
            Add Organizer
          </Button>
        )}
        <div style={{ width: "300px" }}>
          <Input
            placeholder="Search"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
          />
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={tableData || []}
        loading={reduxLoading}
        bordered
        rowKey="_id"
        scroll={{ x: true }}
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions: ["10", "20", "100"],
          defaultPageSize: 100, // Added to ensure 100 rows by default
        }}
      />

      <CreateCredentialForm
        visible={visible}
        onCancel={() => {
          setEditingRecord(null);
          setVisible(false);
        }}
        setVisible={setVisible}
        loading={loading}
        editingRecord={editingRecord} // Pass the editing record
      />
    </div>
  );
};

export default CreateCrdential;
